import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import type { FirebaseApp } from 'firebase/app';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import {
  TemplateSchema,
  TemplateCreateInputSchema,
  TemplateUpdateInputSchema,
  type Template,
  type TemplateCreateInput,
  type TemplateUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import {
  type CountOptions,
  type ListOptions,
  runAverage,
  runCount,
  runList,
  runSum,
} from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `template` (modele de fiche, org-wide).
 *
 * Replique le PATRON `project` pour les huit verbes du socle : l'entree est
 * VALIDEE par Zod (`TemplateCreateInputSchema`) AVANT toute ecriture Firestore,
 * les champs standard sont remplis par le SDK, les requetes `list` sont cadrees
 * par orgId.
 *
 * Deux verbes en plus, qui sont la raison d'etre de la famille :
 *   - `fromRecord` : enregistre une fiche EXISTANTE comme modele, avec les
 *     FICHES LIEES que l'ecran a lues (`children`) ;
 *   - `instantiate` : cree une fiche (et ses enfants) A PARTIR d'un modele.
 *
 * Le calcul de ces deux verbes vit dans des fonctions PURES exportees ici
 * (`templateFieldsFromRecord`, `templateChildFromRecord`, `avecIdsDEnfants`,
 * `poserParChemin`, `sansValeursVides`, `avecNomDeSecours`, `planInstantiation`,
 * `runInstantiation`), meme doctrine que `planListQuery` / `runList` : ce qui
 * decide se prouve par un test `node --test`, sans emulateur ni projet Firebase.
 *
 * CE QUE CE FICHIER TIENT SUR LES ENFANTS, et qui se trompe en silence :
 *   - un enfant porte un `id` STABLE dans le modele, pose ICI a chaque ecriture
 *     (`create` et `update` passent par `avecIdsDEnfants`) : c'est lui qui rend
 *     une case d'enfant adressable a l'ecran. Il ne part JAMAIS a la creation
 *     de la fiche reelle, ce n'est pas un champ metier ;
 *   - son `linkKey` est un CHEMIN DE STOCKAGE (`assetId` ou
 *     `customFields.materielId`) : il est retire des champs a l'enregistrement
 *     (`templateChildFromRecord`) et repose sur la racine CREEE a
 *     l'instanciation (`poserParChemin`), sac compris.
 */

export type { TemplateCreateInput };

/** Nom de la collection Firestore de la famille template. */
export const TEMPLATES_COLLECTION = 'templates';

/** Le SDK recontrôle le catalogue serveur avant la première création du modèle et de ses enfants. */
export function createTemplatePlanValidator(app: FirebaseApp, orgId: string, emulator?: EmulatorEndpoint, localFunctionsConfigured = true) {
  let call: ReturnType<typeof httpsCallable<{ orgId: string; writes: InstantiationWrite[] }, { validated: boolean }>> | undefined;
  return async (plan: InstantiationPlan): Promise<void> => {
    if (!localFunctionsConfigured) throw new Error('Emulateur Functions absent de la configuration locale.');
    if (!call) {
      const functions = getFunctions(app, 'europe-west1');
      if (emulator) connectFunctionsEmulator(functions, emulator.host, emulator.port);
      call = httpsCallable(functions, 'validerPlanModele');
    }
    const result = await call({ orgId, writes: [plan.root, ...plan.children] });
    if (result.data.validated !== true) throw new Error('Le modèle n’a pas pu être validé avant création.');
  };
}

/**
 * LES FAMILLES QU'UN MODELE NE RECOPIE PAS. Un contenu (`contents`) porte sa
 * charge dans des MORCEAUX, hors de sa fiche : un modele tire de la fiche
 * recopierait un nombre de morceaux sans les morceaux, et un plan recurrent,
 * qui ecrit cote serveur sans les rules, ferait naitre un contenu illisible.
 * Une surface qui veut un contenu « modele » le COPIE (`sdk.contents.copy`).
 * Le moteur des plans refuse la meme famille (`familleCible`,
 * functions/src/recurrence/moteur.ts). Une CONVERSATION non plus : son acces
 * (ses participants, son ancre) est a elle, et un modele le recopierait tel
 * quel chez quelqu'un d'autre (`docs/socle/acces-par-fiche.md`).
 */
export const FAMILLES_SANS_MODELE: ReadonlySet<string> = new Set(['contents', 'conversations']);

function exigerFamilleAModele(family: string): void {
  if (FAMILLES_SANS_MODELE.has(family)) {
    throw new Error(
      `Modele : la famille "${family}" ne se met pas en modele (sa charge vit hors de la fiche). ` +
        'Copiez le contenu (`sdk.contents.copy`) au lieu d\'en tirer un modele.',
    );
  }
}

/**
 * Les champs STANDARD du socle (`baseEntityFields`, @taki/data-model), poses
 * par le SDK a chaque creation. Un modele ne les recopie JAMAIS : ils portent
 * l'histoire de la fiche d'origine (son id, sa date de creation, son auteur),
 * pas son contenu metier. `customFields`, lui, N'EST PAS dans cette liste :
 * c'est du metier, il fait partie du modele.
 */
const STANDARD_FIELDS = [
  'id',
  'orgId',
  'createdAt',
  'updatedAt',
  'createdBy',
  'updatedBy',
  'archivedAt',
  'deletedAt',
  'schemaVersion',
  // La PHOTO de reference est l'id d'un fichier rattache a la fiche d'origine :
  // la recopier ferait pointer la nouvelle fiche sur ce fichier (et le retirer
  // la-bas). `couleur`, elle, est du metier et suit le modele.
  'photo',
] as const;

/**
 * LE PREFIXE DU SAC, et pourquoi un CHEMIN DE STOCKAGE existe : un champ d'une
 * fiche vit soit a la racine du document (`assetId`), soit dans le sac de
 * l'app (`customFields.materielId`, doctrine 6.7). Les deux s'ecrivent pareil
 * dans une porte (`liens[].par`), donc `linkKey` porte le CHEMIN, jamais la
 * seule clef : un lien range dans le sac qu'on ecrirait a plat donnerait un
 * enfant que la carte liee ne montre jamais, sans une seule erreur.
 *
 * Un seul niveau de sac, volontairement : `customFields` est plat (les valeurs
 * sont des primitives, cf. `customFieldValueSchema`), il n'y a pas de sac dans
 * le sac a atteindre.
 */
const PREFIXE_SAC = 'customFields.';

/** Un objet nu qu'on peut recopier clef par clef (le sac `customFields` en est un). */
function estUnSac(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Ce qu'un repository cible doit savoir faire pour qu'un modele s'y instancie :
 * creer, et rendre l'id du document cree. C'est la surface MINIMALE, et c'est
 * volontaire : elle est satisfaite par les 55 repositories du SDK sans qu'aucun
 * ne bouge, et elle se fabrique en trois lignes dans un test.
 */
export interface TemplateTargetRepository {
  create(input: Record<string, unknown>): Promise<{ id: string }>;
}

/**
 * Resout un repository par sa FAMILLE (la clef dans `TakiSdk`), ou `null` si la
 * famille n'existe pas.
 *
 * Injecte plutot qu'importe, pour une raison de dependances : sans ca, ce
 * fichier importerait les 55 repositories du SDK, et chaque app embarquerait
 * les 55 familles pour instancier un modele de projet. `createTakiSdk` passe
 * une fermeture qui lit l'objet SDK deja construit.
 */
export type ResolveRepository = (family: string) => TemplateTargetRepository | null;

/** Ce qu'un modele donne a enregistrer comme modele rend : le sous-type cible et les champs metier. */
export interface TemplateFieldsFromRecord {
  targetType: string;
  fields: Record<string, unknown>;
}

/**
 * Extrait d'une fiche EXISTANTE ce qui fait un modele. Fonction PURE : aucune
 * dependance Firestore, aucun acces disque.
 *
 * Trois gestes, et rien d'autre :
 *   - les champs STANDARD sont retires (cf. `STANDARD_FIELDS` ci-dessus) ;
 *   - `type` sort vers `targetType` : c'est le sous-type que les fiches creees
 *     depuis ce modele porteront, alors que le `type` du MODELE porte la
 *     famille cible (cf. l'en-tete de `template.ts`) ;
 *   - tout le reste est garde TEL QUEL, `customFields` compris.
 *
 * On ne filtre pas les valeurs nulles ni les tableaux vides : ce que la fiche
 * porte est ce que le modele reproduira. Un champ qu'on ne veut pas dans le
 * modele se retire cote appelant, pas ici en devinant.
 */
export function templateFieldsFromRecord(
  record: Record<string, unknown>,
): TemplateFieldsFromRecord {
  const type = record['type'];
  if (typeof type !== 'string' || type.length === 0) {
    throw new Error(
      'templateFieldsFromRecord : la fiche n\'a pas de `type` (chaine non vide). ' +
        'Le `type` de la fiche devient le `targetType` du modele : sans lui, les fiches ' +
        'creees depuis ce modele naitraient sans sous-type, et aucune porte ne les montrerait.',
    );
  }

  const retires = new Set<string>([...STANDARD_FIELDS, 'type']);
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (retires.has(key)) continue;
    fields[key] = value;
  }

  return { targetType: type, fields };
}

/**
 * Un enfant a enregistrer, tel que l'ECRAN l'a lu : la fiche liee TELLE
 * QUELLE, champs standard compris. Le tri se fait ici, pas a l'ecran.
 */
export interface TemplateChildFromRecordInput {
  /** Clef du repository cible dans `TakiSdk`, au PLURIEL (`careRecords`). */
  family: string;
  /** Sous-type de la porte liee. Le `type` porte par `record` GAGNE s'il est une chaine non vide. */
  type: string;
  /** CHEMIN DE STOCKAGE du champ de lien (cf. `PREFIXE_SAC`), retire des champs ici. */
  linkKey: string;
  /** La fiche liee telle qu'elle a ete lue par le port des liens. */
  record: Record<string, unknown>;
}

/** Ce qu'un enfant vaut dans un modele : la forme d'une entree de `children`, sans son id. */
export interface TemplateChildFields {
  family: string;
  type: string;
  linkKey: string;
  fields: Record<string, unknown>;
}

/**
 * Extrait d'une FICHE LIEE ce qu'un modele en retient. Fonction PURE, meme
 * doctrine que `templateFieldsFromRecord` juste au-dessus, et memes retraits :
 *   - les champs STANDARD sortent : ils portent l'histoire de la fiche lue ;
 *   - `type` sort des champs, il devient le `type` de l'enfant ;
 *   - LE CHAMP DE LIEN SORT AUSSI, par son CHEMIN (racine ou sac). Il pointe la
 *     fiche racine D'ORIGINE : le garder ferait naitre chaque enfant rattache a
 *     l'ancienne fiche, donc invisible sous la nouvelle, sans une seule erreur.
 *     C'est `runInstantiation` qui le repose, sur la racine CREEE.
 *
 * LE `type` DE LA FICHE GAGNE sur celui du lien, et c'est voulu : la porte liee
 * declare le sous-type qu'elle ATTEND, la fiche lue dit ce qu'elle EST. Sans
 * `type` ni d'un cote ni de l'autre, on leve, pour la meme raison que
 * `templateFieldsFromRecord` : une fiche sans sous-type naitrait invisible de
 * toutes les portes.
 *
 * Tout le reste est garde TEL QUEL, `customFields` compris (c'est du metier).
 * Ce que l'ecran ne veut pas dans le modele, il le retire AVANT d'appeler ici :
 * le socle en retire deja les liens ENTRANTS de la porte liee, on ne devine
 * rien de plus.
 */
export function templateChildFromRecord(input: TemplateChildFromRecordInput): TemplateChildFields {
  const typeDeLaFiche = input.record['type'];
  const type =
    typeof typeDeLaFiche === 'string' && typeDeLaFiche.length > 0 ? typeDeLaFiche : input.type;
  if (typeof type !== 'string' || type.length === 0) {
    throw new Error(
      'templateChildFromRecord : cet enfant n\'a de `type` nulle part (ni sur la fiche liee, ' +
        'ni sur le lien de la porte). Une fiche creee sans sous-type n\'apparait dans aucune ' +
        'porte : elle serait ecrite, puis introuvable.',
    );
  }

  const retires = new Set<string>([...STANDARD_FIELDS, 'type']);
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input.record)) {
    if (retires.has(key)) continue;
    fields[key] = value;
  }

  // Le champ de lien, par CHEMIN : a la racine, ou dans le sac (qui RESTE, meme
  // vide : ses autres clefs sont du metier que le modele doit reproduire).
  if (input.linkKey.startsWith(PREFIXE_SAC)) {
    const sac = fields['customFields'];
    if (estUnSac(sac)) {
      const { [input.linkKey.slice(PREFIXE_SAC.length)]: _lien, ...reste } = sac;
      fields['customFields'] = reste;
    }
  } else {
    delete fields[input.linkKey];
  }

  return { family: input.family, type, linkKey: input.linkKey, fields };
}

/**
 * Rend une COPIE des enfants ou chacun porte un `id`. Fonction PURE.
 *
 * LES IDS DEJA POSES SONT GARDES, jamais reecrits : l'id d'un enfant est ce qui
 * rend sa case adressable a l'ecran (edition en place, pile d'annulation,
 * selection avant un retrait). En reposer des neufs a chaque `update` ferait
 * tomber la selection en cours et une annulation viserait un enfant qui
 * n'existe plus, sans que rien ne le dise.
 *
 * UN `id` VIDE COMPTE POUR ABSENT : le schema l'exige en `min(1)`
 * (@taki/data-model, template.ts), une chaine vide ferait refuser l'ecriture
 * entiere sur une clef que personne n'a remplie.
 *
 * `crypto.randomUUID()` : present partout ou ce SDK tourne (navigateurs et
 * Node 22). Aucun prefixe, cet id ne se lit nulle part, il s'utilise.
 */
export function avecIdsDEnfants<E extends { id?: string }>(
  children: readonly E[],
): Array<E & { id: string }> {
  return children.map((child) => ({
    ...child,
    id: typeof child.id === 'string' && child.id.length > 0 ? child.id : crypto.randomUUID(),
  }));
}

/**
 * Pose `valeur` au CHEMIN DE STOCKAGE `chemin` et rend une COPIE. Fonction
 * PURE : c'est elle qui rattache un enfant a sa racine (`runInstantiation`).
 *
 * A la racine quand le chemin est une simple clef ; DANS LE SAC quand il
 * commence par `customFields.`, et le sac existant est alors FUSIONNE, jamais
 * remplace : un enfant range ses champs sur-mesure dans ce meme sac, l'ecraser
 * pour y poser le lien les effacerait tous d'un coup. Sac absent : il est cree.
 */
export function poserParChemin(
  input: Record<string, unknown>,
  chemin: string,
  valeur: unknown,
): Record<string, unknown> {
  if (!chemin.startsWith(PREFIXE_SAC)) return { ...input, [chemin]: valeur };
  const sac = input['customFields'];
  return {
    ...input,
    customFields: {
      ...(estUnSac(sac) ? sac : {}),
      [chemin.slice(PREFIXE_SAC.length)]: valeur,
    },
  };
}

/** Une ecriture du plan d'instanciation : la famille cible et les donnees a creer. */
export interface InstantiationWrite {
  /** Clef du repository cible dans `TakiSdk` (`projects`, `tasks`, `assets`). */
  family: string;
  /** Les donnees passees telles quelles a `create`. */
  input: Record<string, unknown>;
}

/**
 * Le plan d'instanciation : la racine, puis les enfants DANS L'ORDRE du modele.
 *
 * Structure `{ root, children }` plutot qu'un tableau plat dont la premiere
 * entree serait speciale : l'id de la racine n'existe qu'apres son ecriture, et
 * c'est lui que chaque enfant attend dans son `linkKey`. Le plan ne peut donc
 * pas etre une simple liste homogene, autant que la forme le dise.
 */
export interface InstantiationPlan {
  root: InstantiationWrite;
  children: Array<InstantiationWrite & { linkKey: string }>;
}

/**
 * UNE CASE VIDE EST UNE CASE NON REMPLIE, pas une valeur a ecrire.
 *
 * LE CAS REEL : un modele qu'on remplit a l'ecran garde une clef pour CHAQUE
 * case touchee, et une case qu'on vide vaut `''`. Or les champs facultatifs du
 * data-model s'ecrivent `z.string().min(1).optional()` : `optional()` accepte
 * l'ABSENCE, jamais la chaine VIDE. Un modele « Camera » dont la reference
 * avait ete effacee refusait donc toute creation, sur une clef que personne
 * n'avait voulu remplir. C'est le cas NORMAL du mode Modeles : on ne remplit
 * qu'une partie des cases.
 *
 * CE N'EST PAS UNE DEVINETTE, c'est une equivalence : sur ces champs-la, `''`
 * n'est ecrivable NULLE PART, ni sur une fiche ni sur un modele. Le retirer ne
 * peut donc effacer aucune valeur qui aurait pu vivre ; il traduit « vide » par
 * « absent », le seul des deux que le schema accepte.
 *
 * ON NE RETIRE QUE LA CHAINE STRICTEMENT VIDE. `'   '` passe `min(1)` : c'est
 * une valeur, sotte mais legale, et la retirer serait corriger la saisie de
 * quelqu'un. `null` reste aussi : plusieurs familles le declarent expres pour
 * dire « connu et vide », ce qui n'est pas la meme chose que « pas rempli ».
 *
 * ON NE DESCEND PAS DANS `customFields`. Ce sac appartient a l'APP, son schema
 * (`customFieldValuesSchema`) accepte deja les chaines vides et les nuls, et
 * une app peut vouloir distinguer « case videe » de « case jamais ouverte ».
 * La regle ne vaut que la ou le schema de FAMILLE decide, c'est-a-dire au
 * premier niveau.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : elle retire de la donnee, donc elle doit
 * etre attaquable par un test sans Firestore.
 */
export function sansValeursVides(input: Record<string, unknown>): Record<string, unknown> {
  const garde: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === '') continue;
    garde[key] = value;
  }
  return garde;
}

/**
 * LE NOM DE SECOURS DE LA FICHE CREEE, et pourquoi il existe.
 *
 * UN MODELE N'EST PAS UNE FICHE COMPLETE, c'est tout son interet : on en
 * fabrique un vide depuis le mode Modeles et on ne remplit QUE ce qui se
 * repete (la marque, l'emplacement, la categorie). Or les champs saisis a
 * l'ecran se rangent la ou le binding les envoie, le plus souvent sous
 * `customFields` : un tel modele n'a AUCUN `name` racine. Les familles qui en
 * exigent un (`asset`, `product`, `worker`, `resource`, `group`) refusaient
 * donc l'ecriture, et l'ecran n'en disait qu'un « ca n'a pas marche ». Le geste
 * central du chantier echouait sur son cas le plus normal.
 *
 * CE QU'ON PREND, ET RIEN D'AUTRE : le NOM DU MODELE. C'est le seul texte que
 * la personne a saisi elle-meme pour designer ce modele, il est garanti non
 * vide (`TemplateSchema`), et il se relit tel quel dans la liste des fiches :
 * un modele « Camera » cree une fiche « Camera », qu'on renomme ensuite comme
 * n'importe quelle autre. On ne DEVINE aucune autre valeur manquante : deviner
 * un statut ou une reference fabriquerait de la donnee que personne n'a
 * demandee.
 *
 * ON NE L'ECRASE JAMAIS : un modele tire d'une vraie fiche
 * (`saveFromRecord`) porte deja son `name`, et c'est LUI qui doit gagner. Le
 * secours ne se pose que sur l'absence ou le vide, `overrides` compris : un
 * appelant qui passe un nom explicite reste maitre.
 *
 * LA CLEF EST POSEE MEME QUAND LA FAMILLE N'EN VEUT PAS, et ce n'est pas un
 * probleme : les schemas d'entites du data-model ne sont pas `.strict()`, une
 * clef inconnue est retiree par `parse()` avant l'ecriture Firestore. Le
 * secours ne peut donc pas salir une famille qui nomme ses fiches autrement.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est une regle qui se trompe en silence
 * (elle peut ecraser un nom voulu), et un test ne la tient que si elle est
 * atteignable sans Firestore.
 */
export function avecNomDeSecours(
  input: Record<string, unknown>,
  nomDuModele: string,
): Record<string, unknown> {
  const nom = input['name'];
  if (typeof nom === 'string' && nom.trim() !== '') return input;
  return { ...input, name: nomDuModele };
}

/**
 * Traduit un modele en plan d'ecritures. Fonction PURE : elle ne touche ni
 * Firestore ni le disque, donc la semantique qui compte (l'orgId et le type de
 * chaque fiche, l'ordre des enfants, ce que les overrides ecrasent) se prouve
 * par un test unitaire.
 *
 * L'ordre des cles de la racine est fige et volontaire :
 * `{ orgId, type, ...fields, ...overrides }`. Les `overrides` passent EN
 * DERNIER, donc l'appelant peut tout corriger, y compris le `type` : c'est
 * l'ecran qui sait quel sous-type il ouvre. Le `linkKey` d'un enfant, lui, est
 * pose APRES ses champs : un modele ne peut pas s'auto-rattacher ailleurs.
 *
 * LES DEUX PASSES DE FIN SONT ORDONNEES : on RETIRE d'abord les cases vides
 * (`sansValeursVides`), on COMBLE le nom ensuite (`avecNomDeSecours`). Dans cet
 * ordre, un `name` qu'on a efface a l'ecran vaut un `name` absent et recoit le
 * nom du modele ; dans l'autre, il aurait ete comble puis jete.
 */
export function planInstantiation(
  template: Template,
  overrides: Record<string, unknown> = {},
): InstantiationPlan {
  const root: InstantiationWrite = {
    family: template.type,
    input: avecNomDeSecours(
      sansValeursVides({
        orgId: template.orgId,
        type: template.targetType,
        ...template.fields,
        ...overrides,
      }),
      template.name,
    ),
  };

  /* LES ENFANTS SUIVENT LA MEME REGLE POUR LES CASES VIDES : ils sortent du
     meme modele et vont vers les memes schemas de famille. Pas de nom de
     secours en revanche : un enfant n'a pas de nom a lui, et emprunter celui
     de la racine appellerait toutes les sous-fiches pareil.

     L'`id` DE L'ENFANT NE TRAVERSE PAS : il nomme l'enfant DANS LE MODELE
     (c'est ce qui rend sa case adressable a l'ecran), il n'est pas un champ
     metier. Recopie sur la fiche creee, il ecraserait l'id que Firestore lui
     donne et deux fiches nees du meme modele porteraient le meme. `input` ne
     prend donc que `orgId`, `type` et les champs. */
  const children = (template.children ?? []).map((child) => ({
    family: child.family,
    linkKey: child.linkKey,
    input: sansValeursVides({
      orgId: template.orgId,
      type: child.type,
      ...child.fields,
    }),
  }));

  return { root, children };
}

/** Ce que rend une instanciation : la fiche racine creee, et ses enfants. */
export interface InstantiationResult {
  id: string;
  family: string;
  children: Array<{ family: string; id: string }>;
}

/**
 * EXECUTE un plan d'instanciation : la racine d'abord, puis les enfants EN
 * SEQUENCE, chacun rattache a la racine par son `linkKey`.
 *
 * TOUTES LES FAMILLES SONT RESOLUES AVANT LA PREMIERE ECRITURE. Une famille
 * inconnue (un modele ecrit a la main, un repository renomme) fait echouer
 * l'instanciation SANS avoir rien cree : decouvrir l'erreur apres la racine
 * laisserait une fiche orpheline a moitie montee, que personne ne saurait
 * distinguer d'une fiche voulue.
 *
 * PAS DE BATCH, et c'est une limite assumee : les repositories du socle ne
 * savent pas ecrire dans un lot (`create` fait un `setDoc` et rend le document
 * projete). Si un ENFANT echoue, la racine et les enfants deja crees RESTENT :
 * l'appelant recoit l'erreur, l'utilisateur voit une fiche incomplete, pas une
 * fiche absente. Le jour ou ca gene, c'est un `writeBatch` dans le socle des
 * repositories, pas un rattrapage ici.
 *
 * LE RATTACHEMENT PASSE PAR `poserParChemin`, jamais par une clef posee a plat :
 * `linkKey` est un CHEMIN DE STOCKAGE, et un lien que la porte range dans
 * `customFields` doit finir DANS le sac. Ecrit a plat, l'enfant serait cree, et
 * la carte liee de la racine resterait vide sans qu'aucune erreur ne le dise.
 */
export async function runInstantiation(
  plan: InstantiationPlan,
  resolveRepository: ResolveRepository,
  validatePlan?: (plan: InstantiationPlan) => Promise<void>,
): Promise<InstantiationResult> {
  /** Le repository de cette famille, ou une erreur qui NOMME la famille. */
  const exige = (family: string): TemplateTargetRepository => {
    exigerFamilleAModele(family);
    const repository = resolveRepository(family);
    if (repository == null) {
      throw new Error(
        `Modele : famille inconnue "${family}". Le \`type\` d'un modele et le \`family\` de ` +
          "ses enfants nomment un repository du SDK (`projects`, `tasks`, `assets`...), " +
          'le meme vocabulaire que la clef `family` de taki.fields.json.',
      );
    }
    return repository;
  };

  const rootRepository = exige(plan.root.family);
  const childRepositories = plan.children.map((child) => exige(child.family));

  await validatePlan?.(plan);

  const root = await rootRepository.create(plan.root.input);

  const children: InstantiationResult['children'] = [];
  for (const [index, child] of plan.children.entries()) {
    // Resolu plus haut, donc present : l'index suit celui du plan.
    const repository = childRepositories[index] as TemplateTargetRepository;
    const created = await repository.create(poserParChemin(child.input, child.linkKey, root.id));
    children.push({ family: child.family, id: created.id });
  }

  return { id: root.id, family: plan.root.family, children };
}

/** Ce qu'il faut pour enregistrer une fiche existante comme modele. */
export interface TemplateFromRecordInput {
  /** Clef du repository de la fiche dans `TakiSdk` (ex. `projects`). */
  family: string;
  /** La fiche telle qu'elle a ete lue (champs standard compris : ils sont retires ici). */
  record: Record<string, unknown>;
  name: string;
  description?: string;
  sourceId?: string;
  /**
   * Les FICHES LIEES a retenir avec la fiche, telles que l'ecran les a lues :
   * elles passent par `templateChildFromRecord` (champs standard, `type` et
   * champ de lien retires) puis recoivent leur id a l'ecriture. Absent = un
   * modele sans enfant, le cas courant (et le seul possible depuis un
   * brouillon, ou aucune fiche liee n'existe encore).
   */
  children?: TemplateChildFromRecordInput[];
}

/**
 * LA RAISON QUI INTERDIT DE RETIRER UN MODELE, ou `null` s'il est libre.
 * Fonction PURE, exportee pour l'ecran et pour le test.
 *
 * UN MODELE CITE PAR UN PLAN RECURRENT NE SE RETIRE PAS (ni archive, ni
 * supprime) : le plan relit son modele a chaque passage, et produirait la nuit
 * depuis un modele absent. Le serveur le suspendrait, mais la personne ne le
 * decouvrirait qu'au matin, loin du geste qui l'a cause. On refuse donc AVANT,
 * et les Security Rules refusent aussi (`plansIds` non vide). Pour liberer le
 * modele, on supprime ses plans : ARCHIVER un plan ne suffit pas.
 */
export function raisonDeProtectionDuModele(nombreDePlans: number): string | null {
  if (!(nombreDePlans > 0)) return null;
  return `Utilisé par ${String(nombreDePlans)} plan(s) récurrent(s) : ce modèle ne peut pas être retiré.`;
}

/**
 * Repository type pour la famille `template`.
 * Toute lecture est validee par `TemplateSchema`, toute ecriture par
 * `TemplateCreateInputSchema` (cage IA : aucun acces Firestore direct hors SDK).
 */
export interface TemplateRepository {
  create(input: TemplateCreateInput): Promise<Template>;
  update(id: string, patch: TemplateUpdateInput): Promise<Template>;
  list(options?: ListOptions): Promise<Template[]>;
  count(options?: CountOptions): Promise<number>;
  /** Somme serveur d'un champ, memes options que `count`. Cf. list-query.ts. */
  sum(field: string, options?: CountOptions): Promise<number>;
  /** Moyenne serveur d'un champ, `null` si aucun document. Cf. list-query.ts. */
  average(field: string, options?: CountOptions): Promise<number | null>;
  get(id: string): Promise<Template | null>;
  /**
   * Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. Refuse un
   * modele cite par un plan (cf. `raisonDeProtectionDuModele`).
   */
  archive(id: string): Promise<Template>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Template>;
  /**
   * Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf.
   * archive-ops.ts. Refuse un modele cite par un plan, comme `archive`.
   */
  remove(id: string): Promise<Template>;
  /**
   * Enregistre une fiche EXISTANTE comme modele, avec les fiches liees que
   * l'ecran a lues (cf. `templateFieldsFromRecord` et `templateChildFromRecord`).
   */
  fromRecord(input: TemplateFromRecordInput): Promise<Template>;
  /** Cree une fiche et ses enfants A PARTIR d'un modele (cf. `runInstantiation`). */
  instantiate(id: string, overrides?: Record<string, unknown>): Promise<InstantiationResult>;
}

/**
 * LIT UN MODELE BRUT (le document Firestore, dates deja converties par le
 * converter) et rend un `Template` valide. Fonction PURE et exportee expres.
 *
 * LES IDS D'ENFANTS MANQUANTS SONT POSES AVANT DE PARSER. `TemplateSchema`
 * EXIGE un id par enfant (c'est ce que le port du socle attend, `EnfantDeModele`
 * dans packages/ui/src/views/contract.ts), mais les modeles ecrits avant ce
 * chantier n'en portent pas : parser tel quel les rendrait tous illisibles
 * d'un coup, sans que personne ait rien change. On complete donc le brut, et
 * la prochaine ecriture (`update`, qui envoie le tableau entier) les persiste.
 *
 * DEUX LECTURES D'UN MEME BRUT SANS ID DONNENT DEUX IDS DIFFERENTS, et c'est
 * assume : un tel modele n'a jamais ete edite enfant par enfant a l'ecran (la
 * surface n'existait pas), et des la premiere ecriture l'id devient stable.
 */
export function lireModele(raw: DocumentData): Template {
  const children = raw['children'];
  const complete = Array.isArray(children)
    ? { ...raw, children: avecIdsDEnfants(children as Array<{ id?: string }>) }
    : raw;
  return TemplateSchema.parse(complete);
}

const templateConverter = createConverter<Template>(lireModele);

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 * @param resolveRepository - rend le repository d'une famille, pour
 *   `instantiate` (cf. `ResolveRepository` : injecté pour ne pas importer les
 *   55 repositories ici).
 */
export function createTemplateRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  resolveRepository: ResolveRepository,
  validatePlan: (plan: InstantiationPlan) => Promise<void>,
): TemplateRepository {
  const col = collection(db, TEMPLATES_COLLECTION).withConverter(templateConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Template>(db, col, TEMPLATES_COLLECTION, getActorId);

  /** Lecture d'un modele par son id, partagee par `get` et `instantiate`. */
  async function readOne(id: string): Promise<Template | null> {
    const snap = await getDoc(doc(col, id));
    if (!snap.exists()) return null;
    return snap.data();
  }

  /**
   * LEVE si le modele est cite par un plan (cf. `raisonDeProtectionDuModele`).
   * Lecture BRUTE, sans converter, pour la raison de `remove` dans
   * archive-ops.ts : un modele que son schema refuserait doit rester retirable
   * quand rien ne le cite. Un modele absent passe : `archiveOps` le dira.
   */
  async function exigerLibre(id: string): Promise<void> {
    const snap = await getDoc(doc(db, TEMPLATES_COLLECTION, id));
    if (!snap.exists()) return;
    const plans: unknown = snap.get('plansIds');
    const raison = raisonDeProtectionDuModele(Array.isArray(plans) ? plans.length : 0);
    if (raison !== null) throw new Error(raison);
  }

  async function create(input: TemplateCreateInput): Promise<Template> {
    // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
    // Firestore. (Même doctrine que le patron project.)
    const fields = TemplateCreateInputSchema.parse(input);

    const actor = getActorId();
    const ref = doc(col);
    const now = new Date();

    // Champs métier optionnels : on ne les écrit que s'ils sont fournis
    // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
    const optional: DocumentData = {};
    if (fields.description !== undefined) optional['description'] = fields.description;
    // LES ENFANTS RECOIVENT LEURS IDS ICI, une seule fois pour tous les
    // appelants (`fromRecord` passe par ce meme `create`) : le schema les
    // accepte sans id A LA LECTURE, pour les modeles d'avant ce chantier, mais
    // aucune ecriture du SDK n'en laisse partir un sans id (cf.
    // `avecIdsDEnfants`).
    if (fields.children !== undefined) optional['children'] = avecIdsDEnfants(fields.children);
    if (fields.sourceId !== undefined) optional['sourceId'] = fields.sourceId;
    if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;
    if (fields.couleur != null) optional['couleur'] = fields.couleur;
    if (fields.photo != null) optional['photo'] = fields.photo;

    const payload: DocumentData = {
      orgId: fields.orgId,
      type: fields.type,
      targetType: fields.targetType,
      name: fields.name,
      fields: fields.fields,
      ...optional,
      // AUCUN PLAN A LA CREATION : les rules refusent un modele qui naitrait
      // avec des plans (il ne s'y ajoute qu'avec l'ecriture du plan, en lot).
      plansIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
      updatedBy: actor,
      archivedAt: null,
      deletedAt: null,
      schemaVersion: CURRENT_SCHEMA_VERSION,
    };

    await setDoc(ref, payload as unknown as Template);

    // setDoc ne résout pas serverTimestamp() : on valide une projection
    // cohérente avec ce qui vient d'être écrit.
    return TemplateSchema.parse({
      id: ref.id,
      ...payload,
      createdAt: now,
      updatedAt: now,
    });
  }

  return {
    create,

    async update(id: string, patch: TemplateUpdateInput): Promise<Template> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = TemplateUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      //
      // `children` passe d'abord par `avecIdsDEnfants` : le socle ajoute un
      // enfant avec son id, mais un modele d'avant ce chantier arrive ici sans
      // aucun id, et il repartirait sans (le tableau est ecrit EN ENTIER, pas
      // fusionne). Ses cases seraient alors inadressables a l'ecran.
      const data = buildUpdateData(
        fields.children === undefined
          ? fields
          : { ...fields, children: avecIdsDEnfants(fields.children) },
      );
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, TEMPLATES_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec TemplateSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`template ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Template[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles. Ici `type` vaut la FAMILLE
      // cible : `list({ type: 'projects' })` rend les modeles de projets, cote
      // serveur (cf. l'en-tete de template.ts).
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async sum(field: string, options?: CountOptions): Promise<number> {
      // Sommer sans charger, meme cadrage que `count` (cf. runSum,
      // list-query.ts). Rend 0 quand aucun document ne passe le filtre.
      return runSum(col, orgId, field, options);
    },

    async average(field: string, options?: CountOptions): Promise<number | null> {
      // `null` quand aucun document ne passe le filtre : c'est ce que rend
      // Firestore, et un zero mentirait (cf. runAverage, list-query.ts).
      return runAverage(col, orgId, field, options);
    },

    async get(id: string): Promise<Template | null> {
      return readOne(id);
    },

    async fromRecord(input: TemplateFromRecordInput): Promise<Template> {
      exigerFamilleAModele(input.family);
      for (const child of input.children ?? []) exigerFamilleAModele(child.family);
      // Le tri des champs est PUR (`templateFieldsFromRecord`) : une fiche sans
      // `type` echoue ici, avant toute ecriture.
      const { targetType, fields } = templateFieldsFromRecord(input.record);

      const optional: Partial<TemplateCreateInput> = {};
      if (input.description !== undefined) optional.description = input.description;
      if (input.sourceId !== undefined) optional.sourceId = input.sourceId;
      // Les FICHES LIEES arrivent telles que l'ecran les a lues : le tri est
      // PUR (`templateChildFromRecord`), et il retire le champ de lien, qui
      // pointe la fiche d'origine. Les ids, eux, sont poses par `create`
      // ci-dessous, le seul endroit qui ecrit.
      if (input.children !== undefined) {
        optional.children = input.children.map((child) => templateChildFromRecord(child));
      }

      // `type` = la famille de la fiche d'origine : c'est ce que le modele
      // classe, et ce sur quoi `list({ type })` filtrera.
      return create({
        orgId,
        type: input.family,
        targetType,
        name: input.name,
        fields,
        ...optional,
      });
    },

    async instantiate(
      id: string,
      overrides?: Record<string, unknown>,
    ): Promise<InstantiationResult> {
      const template = await readOne(id);
      if (template == null) {
        throw new Error(
          `template ${id} introuvable : impossible de creer une fiche depuis ce modele. ` +
            'Il a peut-etre ete supprime ailleurs, rechargez la liste des modeles.',
        );
      }
      // Plan PUR puis execution : cf. `planInstantiation` et `runInstantiation`
      // (l'ordre des enfants, ce que les overrides ecrasent, et le fait qu'une
      // famille inconnue n'ecrit RIEN sont prouves sans Firestore).
      return runInstantiation(planInstantiation(template, overrides), resolveRepository, validatePlan);
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut. Archiver et
    // supprimer passent d'abord par le verrou des plans (`exigerLibre`) : un
    // lecteur de plus, sur un geste rare et unitaire.
    ...archiveOps,

    async archive(id: string): Promise<Template> {
      await exigerLibre(id);
      return archiveOps.archive(id);
    },

    async remove(id: string): Promise<Template> {
      await exigerLibre(id);
      return archiveOps.remove(id);
    },
  };
}
